import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Sparkles, Compass, PhoneCall } from 'lucide-react';

/*
 * /start — the landing route for ad/QR traffic (Old Brooklyn News funnel).
 * Design rules (first-touch research): repeat the ad's promise verbatim in the
 * first line; put a real interactive question on screen with zero taps to reach
 * it; plain language a non-technical business owner understands; 48px+ touch
 * targets; generous feedback on wrong answers; no signup ask anywhere.
 */

const CHOICES = [
  {
    id: 'nothing',
    label: 'Nothing — looks good, send it',
    correct: false,
    feedback:
      'It reads well — that’s exactly the trap. Look again at “certified gluten-free by the Ohio Restaurant Health Board.” There is no such certification. The AI invented an official-sounding credential, and a customer with celiac disease could be seriously harmed by that promise.',
  },
  {
    id: 'invented',
    label: 'It made up the “certification”',
    correct: true,
    feedback:
      'Exactly right. “Certified gluten-free by the Ohio Restaurant Health Board” sounds official — but that certification doesn’t exist. The AI invented it to sound helpful. You just caught an AI hallucination: the #1 skill for using these tools safely in a business.',
  },
  {
    id: 'long',
    label: 'It’s too wordy',
    correct: false,
    feedback:
      'The length is fine — the real problem is bigger: “certified gluten-free by the Ohio Restaurant Health Board” is an invented credential. No such certification exists. The AI made it up to sound helpful, and a business that sends this could be liable for the promise.',
  },
];

export default function Start() {
  const [picked, setPicked] = useState<string | null>(null);
  const choice = CHOICES.find((c) => c.id === picked);

  return (
    <div className="max-w-xl mx-auto">
      {/* The ad's promise, word for word */}
      <div className="text-center mb-8">
        <p className="text-accent-green font-semibold text-lg mb-2">
          Free, hands-on AI training. No account. No catch.
        </p>
        <p className="text-gray-400 text-sm">
          From the Cleveland father-and-son team at{' '}
          <a href="https://northcoast.ai" target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">northcoast.ai</a>
        </p>
      </div>

      {/* Zero-tap first exercise */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 mb-8">
        <p className="text-white font-semibold mb-3">
          Try this. A restaurant owner asked AI to answer a customer&rsquo;s question about
          gluten-free options. The AI wrote:
        </p>
        <blockquote className="bg-background-secondary border-l-4 border-accent-blue rounded-r-lg p-4 text-gray-300 text-sm mb-4">
          &ldquo;Great news! All of our pasta dishes can be made gluten-free, and our kitchen is
          certified gluten-free by the Ohio Restaurant Health Board. You have nothing to worry
          about!&rdquo;
        </blockquote>
        <p className="text-white font-semibold mb-3">What&rsquo;s wrong with this reply?</p>

        {!picked ? (
          <div className="space-y-3">
            {CHOICES.map((c) => (
              <button
                key={c.id}
                onClick={() => setPicked(c.id)}
                className="w-full min-h-[52px] text-left px-4 py-3 bg-gray-700/60 hover:bg-gray-600 border border-gray-600 rounded-xl text-gray-100 transition-colors"
              >
                {c.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="animate-fade-in">
            <div className={`rounded-xl p-4 border ${choice?.correct ? 'bg-green-500/10 border-green-500/40' : 'bg-blue-500/10 border-blue-500/40'}`}>
              <p className="font-semibold text-white mb-2">
                {choice?.correct ? 'You caught it.' : 'Most people miss this one.'}
              </p>
              <p className="text-gray-200 text-sm">{choice?.feedback}</p>
            </div>
            <p className="text-gray-300 text-sm mt-4">
              That habit &mdash; <em>check the claim before it leaves your hands</em> &mdash; is what we
              teach. Google will show you how to <em>use</em> AI for free, and you should let them. We
              teach the part they leave out: how to tell when it&rsquo;s wrong before it costs you a
              customer.
            </p>
          </div>
        )}
      </div>

      {/* Paths — only after or alongside the exercise, never gating it */}
      <div className="space-y-3 mb-10">
        <p className="text-white font-semibold">What would help you most right now?</p>
        <Link
          to="/shift"
          className="flex items-center gap-3 min-h-[56px] px-4 py-3 bg-accent-orange text-slate-900 font-medium rounded-xl hover:opacity-90 transition-opacity"
        >
          <Sparkles className="w-5 h-5 shrink-0" aria-hidden="true" />
          <span>Get better at spotting AI mistakes <span className="opacity-70">&mdash; 3-minute challenge</span></span>
        </Link>
        <Link
          to="/fix-it"
          className="flex items-center gap-3 min-h-[56px] px-4 py-3 bg-accent-blue text-slate-900 font-medium rounded-xl hover:opacity-90 transition-opacity"
        >
          <Wrench className="w-5 h-5 shrink-0" aria-hidden="true" />
          <span>Fix a computer problem with AI <span className="opacity-70">&mdash; free step-by-step guide</span></span>
        </Link>
        <Link
          to="/"
          className="flex items-center gap-3 min-h-[56px] px-4 py-3 bg-gray-700 text-white font-medium rounded-xl hover:bg-gray-600 transition-colors"
        >
          <Compass className="w-5 h-5 shrink-0" aria-hidden="true" />
          <span>Just show me everything</span>
        </Link>
      </div>

      {/* Trust line + human option */}
      <div className="text-center border-t border-gray-800 pt-6">
        <p className="text-gray-400 text-sm mb-3">
          No email required. No credit card. Ever, for this part.
        </p>
        <a href="tel:+12169995765" className="inline-flex items-center gap-2 text-accent-blue hover:underline text-sm">
          <PhoneCall className="w-4 h-4" aria-hidden="true" /> Questions? Call or text (216) 999-5765
        </a>
      </div>
    </div>
  );
}
